import validate from "validate.js";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import utc from "dayjs/plugin/utc";
dayjs.extend(customParseFormat);
dayjs.extend(utc);
import { isNullOrUndefined } from "./DefineFunction";
// for datetime validation
// ref: https://validatejs.org/#validators-datetime
validate.extend(validate.validators.datetime, {
    parse: (value, options) => {
        let date = options.format ? dayjs(value, options.format, true) : dayjs(value);
        return date.utc(true).valueOf();
    },
    format: (value, options) => {
        if (isNaN(value)) {
            return "";
        }
        let date = dayjs(value);
        return options.format ? date.format(options.format) : date.format();
    }
});
/** The date validator is just a shorthand for the datetime validator with the dateOnly option set to true. */
export function date(options = null) {
    if (options === null) {
        options = {
            dateOnly: true
        };
    }
    else {
        options = validate.extend(options, {
            dateOnly: true
        });
    }
    return datetime(options);
}
export function datetime(options = null) {
    let label = "日時";
    if (options?.dateOnly) {
        label = "日付";
    }
    let defaults = {
        message: `不正な${label}です`,
        tooEarly: `%{date}より前の${label}を入力してください`,
        tooLate: `%{date}より後の${label}を入力してください`
    };
    return validate.extend(defaults, options);
}
export function email(options = null) {
    let defaults = {
        message: "メールアドレスとして不正です"
    };
    return validate.extend(defaults, options);
}
export function equality(options) {
    options = validate.extend({}, options);
    options.message = options.label ? "%{label}と一致しません" : "%{attribute}と一致しません";
    return options;
}
export function exclusion(options) {
    let defaults = {
        message: "^%{value}は許可されていません"
    };
    return validate.extend(defaults, options);
}
export function format(options) {
    let defaults = {
        message: "無効な値です"
    };
    return validate.extend(defaults, options);
}
export function regExpInvalidCharPattern() {
    return format({
        /*以下は読み取れない文字が多いので含めない事にする
         *                            ^^^^^^^^^^^
         * CJK統合漢字拡張A  : \u3400-\u4DBF
         * CJK統合漢字       : \u4E00-\u9FFF
         * CJK互換漢字       : \uF900-\uFAFF
         * CJK統合漢字拡張B  : \u20000-\u2FFFF
         * CJK部首補助       :\u2E80-\u2FDF
         */
        //------------------------------------------------------
        /*以下は読み取れるものの詳細
         * スペース、英数字アンダースコア、記号、ひらがな、カタカナ、第1水準漢字、第2水準漢字
         * 記号、機種依存文字(Sjisで扱える物)
         * ①-⑳
         * Ⅰ-Ⅹ
         * 全角 アルファベット大文字／小文字／数字
         */
        pattern: new RegExp(/[\s\w\u2010-\u2015\u3041-\u3096\u30A1-\u30FC\u3400-\u9FFF\uF900-\uFAFF\uD840-\uD87F々〇■\u0020-\u007E\u3000-\u303F\u3040-\u309F\u2160-\u2169\u2460-\u2474\uFF41-\uFF5A\uFF21-\uFF3A\uFF10-\uFF19\uFF0D]*/),
        message: "使用できない文字が含まれています。"
    });
}
export function inclusion(options) {
    let defaults = {
        message: "^%{value}はリストに含まれていません"
    };
    return validate.extend(defaults, options);
}
/** 文字列長を検証します */
export function length(options) {
    let defaults = {
        message: "has an incorrect length",
        wrongLength: "%{count}文字で入力してください",
        tooShort: "%{count}文字以上で入力してください",
        tooLong: "%{count}文字以下で入力してください"
    };
    return validate.extend(defaults, options);
}
export function numericality(options = null) {
    let defaults = {
        notGreaterThan: "%{count}より大きい数値を入力してください",
        notGreaterThanOrEqualTo: "%{count}以上の数値を入力してください",
        notEqualTo: "%{count}を入力してください",
        notLessThan: "%{count}より小さい数値を入力してください",
        notLessThanOrEqualTo: "%{count}以下の数値を入力してください",
        notDivisibleBy: "%{count}で割り切れる値を入力してください",
        notOdd: "奇数を入力してください",
        notEven: "偶数を入力してください"
    };
    return validate.extend(defaults, options);
}
//-----------------------------------------
// presence
export function presence(allowEmpty = false, message = "") {
    return {
        allowEmpty,
        message: message || "必須です"
    };
}
export function url(options = null) {
    let defaults = {
        message: "URLとして不正です"
    };
    return validate.extend(defaults, options);
}
//------------------------------------------
// custom validators
// // カスタムバリデータも追加できる
// validator.validators.custom = function(value: any, options: any, key: any, attributes: any) {
//   console.log(value);
//   console.log(options);
//   console.log(key);
//   console.log(attributes);
//   return undefined;
// };
validate.validators.range = function (value, options, key, attributes) {
    let min = options.min;
    let max = options.max;
    if (value == null || min == null || max == null) {
        return null;
    }
    if (min <= value && value <= max) {
        return null;
    }
    return options.message;
};
export function range(min, max, message = "") {
    return {
        min,
        max,
        message: message || `${min}～${max}で入力してください`
    };
}
validate.validators.step = function (value, options, key, attributes) {
    if (value == null || options.step == null) {
        return null;
    }
    let resultValue = value % options.step;
    if (resultValue === 0) {
        return null;
    }
    let a = value - resultValue;
    let b = a + options.step;
    let message = `無効な値です。有効な値として最も近いのは${a}と${b}です。`;
    return message;
};
export function step(value) {
    return {
        step: value
    };
}
export function maxLength(maximum, message = "") {
    return {
        maximum,
        tooLong: message || "%{count} 文字以内で入力してください"
    };
}
validate.validators.since = function (value, options, key, attributes) {
    let until = attributes[options.untilFieldName];
    if (!value || !until) {
        return null;
    }
    let invalid = Date.parse(value) > Date.parse(until);
    if (!invalid) {
        return null;
    }
    return options.message;
};
export function since(untilFieldName, message = "") {
    return {
        untilFieldName,
        message: message || "終了以前の日時を入力してください"
    };
}
validate.validators.greaterThanIntervalValue = function (value, options, key, attributes) {
    let sinceValue = options.since;
    if (!value || !sinceValue) {
        return null;
    }
    const regex = /:/g;
    let sinceArray = sinceValue.replace(regex, ".").split(".");
    let untilArray = value.replace(regex, ".").split(".");
    for (let i = 0; i < sinceArray.length; i++) {
        const since = Number.parseInt(sinceArray[i], 10);
        const until = Number.parseInt(untilArray[i], 10);
        if (until > since) {
            return null;
        }
    }
    return options.message;
};
export function greaterThanIntervalValue(name, message = "") {
    return {
        since: name,
        message: message || "開始時刻より大きい時刻を入力してください"
    };
}
validate.validators.comparator = function (value, options, key, attributes) {
    if (!value) {
        return null;
    }
    let currentPassword = attributes.current;
    let newPassword = attributes.new;
    let confirmPassword = attributes.confirm;
    let errFlg = false;
    if (key === "new") {
        errFlg = currentPassword === newPassword;
    }
    else if (key === "confirm") {
        errFlg = newPassword !== confirmPassword;
    }
    if (!errFlg) {
        return null;
    }
    return options.message;
};
export function comparator(message = "") {
    return {
        message: message || "一致していません"
    };
}
validate.validators.dateFormat = function (value, options, key, attribute) {
    if (isNullOrUndefined(value)) {
        return null;
    }
    // 月までのinputの場合1日を結合後チェック
    if (options.toMonth) {
        value += "/01";
    }
    if (!value.match(/^([12]\d{3}\/(0?[1-9]|1[0-2])\/(0?[1-9]|[12][0-9]|3[01]))?$/)) {
        return options.message;
    }
    // 2022/1/32等の不正な日付はInvalid Date
    let date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return options.message;
    }
    // 2022/11/31などをDateに直した際に、2022/12/01になることを利用した日付チェック
    let sp = value.split("/");
    let year = Number(sp[0]);
    let month = Number(sp[1]);
    let day = Number(sp[2]);
    if (year !== date.getFullYear() || month !== date.getMonth() + 1 || day !== date.getDate()) {
        return options.message;
    }
    return null;
};
export function dateFormat(message = "", toMonth = false) {
    return {
        message,
        toMonth
    };
}
/** 指定した日付項目より後の日付か比較チェック */
validate.validators.isDateBeforeStart = function (value, options, key, attributes) {
    if (value === null || value === "" || attributes[options.targetPropertyName] === null) {
        return null;
    }
    let startAt = new Date(attributes[options.targetPropertyName]);
    if (Number.isNaN(startAt.getTime())) {
        return null;
    }
    let endAt = new Date(value);
    if (Number.isNaN(endAt.getTime())) {
        return null;
    }
    if (isBeforeDate(startAt, endAt)) {
        return options.message;
    }
    return null;
};
/** 指定した日付項目より前の日付か比較チェック */
validate.validators.isDateAfterStart = function (value, options, key, attributes) {
    if (value === null || value === "" || attributes[options.targetPropertyName] === null) {
        return null;
    }
    let startAt = new Date(attributes[options.targetPropertyName]);
    if (Number.isNaN(startAt.getTime())) {
        return null;
    }
    let endAt = new Date(value);
    if (Number.isNaN(endAt.getTime())) {
        return null;
    }
    if (isBeforeDate(endAt, startAt)) {
        return options.message;
    }
    return null;
};
/** 日付の前後チェック startAtよりendAtが後かどうか */
export function isBeforeDate(startAt, endAt) {
    // 年が後
    if (endAt.getFullYear() > startAt.getFullYear()) {
        return false;
    }
    // 年が前
    if (endAt.getFullYear() < startAt.getFullYear()) {
        return true;
    }
    // 年は同じで月が後
    if (endAt.getMonth() > startAt.getMonth()) {
        return false;
    }
    // 年が同じで月が前
    if (endAt.getMonth() < startAt.getMonth()) {
        return true;
    }
    // 年月同じで日が後
    if (endAt.getDate() > startAt.getDate()) {
        return false;
    }
    // 年月同じで日が前
    if (endAt.getDate() < startAt.getDate()) {
        return true;
    }
    // 年月日一緒
    return false;
}
/**
 * @param message message
 * @param targetPropertyName 比較対象のプロパティ名
 */
export function isDateBeforeStart(message, targetPropertyName) {
    return {
        message,
        targetPropertyName
    };
}
/**
 * @param message message
 * @param startAtPropetryName 比較対象のプロパティ名
 */
export function isDateAfterStart(message, targetPropertyName) {
    return {
        message,
        targetPropertyName
    };
}
